import "./case.scss";

import moment from "moment";
import { useEffect, useState } from "react";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { PaymentPanel } from "@components/billing/PaymentPanel";
import Condition from "@components/Condition";
import { useIdentityVerificationConfirmedBanner } from "@contexts/identityVerificationConfirmedBanner/IdentityVerificationConfirmedBannerProvider";
import { useIdentityVerificationPanel } from "@contexts/identityVerificationPanel/IdentityVerificationPanelProvider";
import useCase from "@hooks/case/useCase";
import useIdentityVerification from "@hooks/user/useIdentityVerification";
import TruckSvg from "@img/orders/truck.svg";
import {
  AlertBanner,
  Button,
  Card,
  Label,
  Link,
  Text,
  Title,
} from "@runwayhealth/runway-components-react";
import * as CasesActions from "@store/cases/casesActions";
import * as casesSlice from "@store/cases/casesSlice";
import { Case as ICase, CaseStatuses, OrderCaseStatuses } from "@types-runway/case";
import { VALID_QUESTIONNAIRE_LENGTH } from "@types-runway/questionnaire";
import { PaymentOption } from "@types-runway/paymentRetry";
import { ErrorEvents, RootEntity } from "@types-runway/state";
import { deriveOrderPaymentState, OrderPaymentState } from "@utils/billing/orderPaymentState";
import { derivePaymentOptions } from "@utils/billing/paymentOptions";
import { PAYMENT_PROVIDER_STRIPE } from "@utils/constantsEnv";
import { describePaymentOutcome } from "@utils/billing/paymentRetryMessages";
import { resolveOrderCaseStatus } from "@utils/case/resolveOrderCaseStatus";

import RunwayPlusNotice from "../Conditions/component/RunwayPlus/RunwayPlusNotice";

type Props = {
  case_: RootEntity<ICase>;
  customPharmacy?: string;
};

const getTrackingLink = (trackingNumber: string, carrier: string) => {
  switch (carrier) {
    case "fedex":
      return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
    case "ups":
      return `https://www.ups.com/track?loc=en_US&requester=QUIC&tracknum=${trackingNumber}/trackdetails`;
    default:
      return `https://tools.usps.com/go/TrackConfirmAction_input?strOrigTrackNum=${trackingNumber}`;
  }
};

const COMPLETE_CONSULTATION = "Complete Consultation";
const SUBMIT_CONSULTATION = "Submit Consultation";

const Case = ({ case_, customPharmacy }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  // Pending actions.
  const [isPendingOrderPayment, setIsPendingOrderPayment] = useState(false);
  const [isPendingCasePayment, setIsPendingCasePayment] = useState(false);
  const [isPendingQuestionnaire, setIsPendingQuestionnaire] = useState(false);
  const [pendingAction, setPendingAction] = useState("");

  const [caseStatus, setCaseStatus] = useState("");

  const [isCustomPharmacy, setIsCustomPharmacy] = useState(false);

  const { fetchCaseDetails } = useCase();
  const { status: identityVerificationStatus } = useIdentityVerification();
  const { open: openIdentityVerificationPanel } = useIdentityVerificationPanel();
  const { isDismissed: isConfirmedBannerDismissed } = useIdentityVerificationConfirmedBanner();

  // Only a case that's actually reached the clinic (has an MDI caseId) can be
  // waiting on the clinician's review — a case still in draft hasn't been sent
  // yet, so there's no physician review for identity verification to gate.
  // Once confirmed, this note shares the dashboard banner's own dismissal —
  // dismissing one dismisses both, rather than leaving a confirmed note
  // pinned to every case after the account-level banner is gone.
  const showIdentityVerificationNote =
    case_.data.caseId !== null &&
    case_.data.case_status !== CaseStatuses.COMPLETED &&
    (identityVerificationStatus === "awaiting_submission" ||
      identityVerificationStatus === "awaiting_confirmation" ||
      (identityVerificationStatus === "confirmed" && !isConfirmedBannerDismissed));

  // Collects case status information.
  // We merge case and order status to show a single status information label.
  useEffect(() => {
    const pharmacy = customPharmacy !== "" && customPharmacy !== null;

    setCaseStatus(resolveOrderCaseStatus(case_.data, pharmacy));
    setIsCustomPharmacy(pharmacy);
  }, [
    case_.data.case_status,
    case_.data.CurexaOrder?.status,
    case_.data.CurexaOrder?.paid,
    case_.data.CurexaOrder?.replacement?.status,
    case_.data.conditions,
    case_.data.trip?.from,
    customPharmacy,
  ]);

  // Collects case and order pending actions.
  useEffect(() => {
    const isPendingOrderPayment =
      deriveOrderPaymentState(case_.data) === OrderPaymentState.PAYMENT_FAILED;

    const isPendingCasePayment = !case_.data.paid && !case_.data.caseId;

    const isPendingQuestionnaire =
      (case_.data.case_questions?.length ?? 0) < VALID_QUESTIONNAIRE_LENGTH;

    setIsPendingOrderPayment(isPendingOrderPayment);
    setIsPendingCasePayment(isPendingCasePayment);
    setIsPendingQuestionnaire(isPendingQuestionnaire);

    if (isPendingOrderPayment) {
      setPendingAction("Complete purchase");
      return;
    }
    if (isPendingQuestionnaire && isPendingCasePayment) {
      setPendingAction(COMPLETE_CONSULTATION);
      return;
    }
    if (isPendingCasePayment) {
      setPendingAction(SUBMIT_CONSULTATION);
      return;
    }
    if (!isPendingCasePayment && !isPendingOrderPayment) {
      setPendingAction("");
      return;
    }
  }, [case_.data.CurexaOrder?.status, case_.data.CurexaOrder?.paid, case_.data.paid]);

  // Checks order sent successfully.
  useEffect(() => {
    if (case_.data.CurexaOrder?.status) {
      setPendingAction("");
    }
  }, [case_.data.CurexaOrder?.status]);

  // Checks case mutation errors.
  useEffect(() => {
    if (case_?.error?.event) {
      switch (case_.error?.event) {
        case ErrorEvents.SEND_CASE:
          callToast(`Could not send case. Error: ${case_.error.message}`);
          break;
        case ErrorEvents.PENDING_INVOICE:
          dispatch(casesSlice.setCasePendingPayment({ caseId: case_.data.id }));
          setIsPendingCasePayment(true);
          setPendingAction("Go to checkout");
          break;
        default:
          callToast(`Unknown error: ${case_.error.message}`);
      }
    }
  }, [case_.error]);

  const callToast = (message: string) => {
    toast(message, {
      position: "top-center",
      autoClose: 3000,
      closeButton: true,
      hideProgressBar: true,
    });
  };

  const paymentMessage = case_.error?.paymentOutcome
    ? describePaymentOutcome(case_.error.paymentOutcome)
    : null;

  const dismissPaymentMessage = () => {
    dispatch(casesSlice.dismissPaymentError({ caseId: case_.data.id }));
  };

  const paymentBanner = paymentMessage && (
    <div role={paymentMessage.variant === "error" ? "alert" : "status"}>
      <AlertBanner variant={paymentMessage.variant} isRounded>
        <AlertBanner.Message>{paymentMessage.text}</AlertBanner.Message>
        <AlertBanner.Buttons>
          <Button
            size="sm"
            iconName="X"
            isIconOnly
            variant="outline"
            aria-label="Dismiss"
            onClick={dismissPaymentMessage}
          />
        </AlertBanner.Buttons>
      </AlertBanner>
    </div>
  );

  const paymentOptions = isPendingOrderPayment ? derivePaymentOptions(case_.data) : [];

  // A card payment on an order without a Stripe invoice is reviewed before it is charged.
  const payOrder = ({ invoiceProvider }: PaymentOption) => {
    if (case_.isPending) {
      return;
    }
    if (invoiceProvider === PAYMENT_PROVIDER_STRIPE) {
      dispatch({
        type: CasesActions.PREPARE_CARD_PAYMENT,
        caseId: case_.data.id,
        orderId: case_.data.CurexaOrder?.order_id,
      });
      return;
    }
    dispatch({
      type: CasesActions.PAY_ORDER,
      caseId: case_.data.id,
      orderId: case_.data.CurexaOrder?.order_id,
      ...(invoiceProvider && { invoiceProvider }),
    });
  };

  const confirmCardPayment = () => {
    dispatch({
      type: CasesActions.PAY_ORDER,
      caseId: case_.data.id,
      orderId: case_.data.CurexaOrder?.order_id,
    });
  };

  const cancelCardPayment = () => {
    dispatch(casesSlice.cardPaymentReviewClosed({ caseId: case_.data.id }));
  };

  const completePendingAction = async () => {
    if (isPendingQuestionnaire) {
      // Get pending case details.
      const caseDetails = await fetchCaseDetails(case_.data.id ?? 0);

      if (!caseDetails) {
        dispatch(casesSlice.newCaseFailure({ message: "Couldn't fetch case details" }));
        return;
      }

      dispatch(casesSlice.newCaseSuccess(caseDetails));
      history.push("/consultation");
      return;
    }

    if (isPendingCasePayment) {
      // Get pending case details.
      const caseDetails = await fetchCaseDetails(case_.data.id ?? 0);

      if (!caseDetails) {
        dispatch(casesSlice.newCaseFailure({ message: "Couldn't fetch case details" }));
        return;
      }

      // Load case.
      dispatch(casesSlice.newCaseSuccess(caseDetails));

      history.push("/checkout");
      return;
    }
  };

  // A declined order's status pill shares the title row, so the title column is wider.
  const titleColumns = isPendingOrderPayment ? 8 : 4;
  const detailColumns = isPendingOrderPayment ? 4 : 5;

  return (
    <Card className="order-card">
      <Container>
        <Row>
          <Col xs={12} sm={12} lg={titleColumns}>
            <div className="case-heading">
              <Title>
                {case_.data.caseId !== null
                  ? `Order #${case_.data.caseId?.split("-")[0]}`
                  : `Case #${case_.data.id}`}
              </Title>
              {isPendingOrderPayment && (
                <span className="case-status case-status--declined" data-testid="case-status">
                  <span aria-hidden="true">!</span> {caseStatus}
                </span>
              )}
            </div>
          </Col>
          {!isPendingOrderPayment && (
            <Col className="centered-text" xs={12} sm={12} lg={2}>
              {case_.data.caseId !== null && (
                <div className="">
                  <Label>{caseStatus}</Label>
                </div>
              )}
            </Col>
          )}
          <Col className="centered-text" xs={12} sm={12} lg={detailColumns}>
            {case_.data?.CurexaOrder?.tracking_number &&
              (caseStatus === OrderCaseStatuses.SHIPPED ||
                caseStatus === OrderCaseStatuses.COMPLETE) && (
                <div className="tracking-number">
                  <span className="tracking-order-carrier-name">
                    <img src={TruckSvg} alt="" />{" "}
                    {case_.data.CurexaOrder?.replacement?.carrier ??
                      case_.data.CurexaOrder?.carrier}{" "}
                    :
                  </span>
                  <Link
                    href={getTrackingLink(
                      case_.data.CurexaOrder?.replacement?.tracking_number ??
                        case_.data.CurexaOrder?.tracking_number,
                      case_.data.CurexaOrder?.replacement?.carrier ??
                        case_.data.CurexaOrder?.carrier
                    )}
                    isBlank
                  >
                    {case_.data.CurexaOrder?.replacement?.tracking_number ??
                      case_.data.CurexaOrder?.tracking_number}
                  </Link>
                </div>
              )}
            {isCustomPharmacy && (
              <div className="local-pharmacy-advice">
                {/* <span>
                <img src={TrackingSvg} alt="" /> {case_.data.CurexaOrder.carrier}
              </span> */}
                <Text>Contact your local pharmacy</Text>
              </div>
            )}
          </Col>
        </Row>
      </Container>
      <hr></hr>
      <div className="alert-banner-container">
        {isCustomPharmacy && caseStatus !== OrderCaseStatuses.CANCELLED && (
          <AlertBanner variant="info" isRounded>
            <AlertBanner.Message>
              Runway cannot guarantee the price or availability of medications not fulfilled by
              Runway.
            </AlertBanner.Message>
          </AlertBanner>
        )}
        {!isPendingOrderPayment && paymentBanner}
        {caseStatus === OrderCaseStatuses.CANCELLED &&
          (case_.data.case_status === CaseStatuses.CANCELLED ? (
            <AlertBanner variant="error" isRounded>
              <AlertBanner.Message>
                No medications were prescribed by your physician. Your order has been cancelled and
                we have refunded your consultation fee.
              </AlertBanner.Message>
            </AlertBanner>
          ) : (
            <AlertBanner variant="error" isRounded>
              <AlertBanner.Message>Your order has been cancelled.</AlertBanner.Message>
            </AlertBanner>
          ))}
        {caseStatus === OrderCaseStatuses.PAUSED && (
          <AlertBanner variant="info" isRounded>
            <AlertBanner.Message>
              Your order is paused, the pharmacy team is working on some adjustments.
            </AlertBanner.Message>
          </AlertBanner>
        )}
        {showIdentityVerificationNote &&
          (identityVerificationStatus === "awaiting_submission" ? (
            <AlertBanner variant="warning" isRounded>
              <AlertBanner.Message>
                <Title as="h3" size="md-bold" noMarginBottom>
                  ID Verification
                </Title>
                <Text size="sm">
                  Your physician requires a photo of your ID for patient verification before your
                  consultation can be finalized - a driver's license, passport or state ID is
                  accepted.{" "}
                  <Link
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      openIdentityVerificationPanel();
                    }}
                  >
                    Upload ID
                  </Link>
                </Text>
              </AlertBanner.Message>
            </AlertBanner>
          ) : identityVerificationStatus === "awaiting_confirmation" ? (
            <AlertBanner variant="info" isRounded>
              <AlertBanner.Message>
                Your ID is on its way — the physician review for this case continues once it's
                confirmed.
              </AlertBanner.Message>
            </AlertBanner>
          ) : (
            <AlertBanner variant="success" isRounded>
              <AlertBanner.Message>
                Your identity is verified — this order isn't waiting on anything from you here.
              </AlertBanner.Message>
            </AlertBanner>
          ))}
      </div>
      {isPendingOrderPayment && (
        <PaymentPanel
          case_={case_.data}
          isPending={case_.isPending}
          options={paymentOptions}
          banner={paymentBanner}
          singleActionLabel={pendingAction}
          onPay={payOrder}
          isAwaitingConfirmation={case_.data.CurexaOrder?.isAwaitingCardConfirmation === true}
          onConfirm={confirmCardPayment}
          onCancel={cancelCardPayment}
        />
      )}
      <div className="order-items-container">
        {case_.data.conditions.map((condition) => {
          return (
            <Condition
              key={condition.suggestedMedication?.id}
              variant="feed"
              condition={condition}
            />
          );
        })}
      </div>
      {case_.data.conditions.length === 0 && case_.data.case_status !== CaseStatuses.COMPLETED ? (
        <RunwayPlusNotice caseId={case_.data.caseId} />
      ) : (
        <div>
          {" "}
          {case_.data.conditions.length === 0 && (
            <p>Case has been completed with no prescriptions.</p>
          )}
        </div>
      )}
      {pendingAction && !isPendingOrderPayment && (
        <Button
          size="sm"
          onClick={completePendingAction}
          iconName="ArrowRight"
          isLoading={case_.isPending}
          disabled={case_.isPending}
        >
          {pendingAction}
        </Button>
      )}
      <p className="case-updated-date">
        Updated{" "}
        {moment().diff(moment(case_.data.updatedAt), "hours") < 24
          ? moment(case_.data.updatedAt).fromNow()
          : moment(case_.data.updatedAt).format("MMM Do, YYYY")}
      </p>
    </Card>
  );
};

export default Case;
